import { TUNING } from '@cloudpearl/config';

const HOLD_KEYS = ['Space', 'ArrowDown', 'ArrowUp', 'KeyS', 'KeyW', 'ShiftLeft', 'ShiftRight'];

/**
 * Unified input: keyboard (WASD / arrows / space), pointer (hold anywhere to dive), device tilt
 * (lean to steer) and gamepads. Without tilt data (no sensor, permission denied, or an insecure
 * http:// page), dragging the holding finger sideways steers instead — one gesture, no zones.
 * Produces one analog steer value and one hold flag, sampled by the fixed-step sim.
 */
export class Input {
  hold = false;
  steer = 0;
  tiltAvailable = false;
  tiltPermission: 'unknown' | 'granted' | 'denied' = 'unknown';
  /** Fired for "start / retry" style taps (pointerdown on the play surface, Space, Enter). */
  onAction: (() => void) | null = null;
  onPause: (() => void) | null = null;
  onNav: ((dir: -1 | 1) => void) | null = null;
  /** True once any touch happened — UI uses it to tailor hints. */
  isTouch = false;
  enabled = false;

  private readonly keys = new Set<string>();
  private keySteer = 0;
  private tiltRaw = 0;
  private tiltNeutral = 0;
  private tiltSmoothed = 0;
  private hasTilt = false;
  private pointers = new Map<number, { x: number; startX: number }>();
  private touchSteer = 0;
  private padHold = false;
  private padSteer = 0;
  private padPrevStart = false;

  constructor(private readonly surface: HTMLElement) {
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.clear);
    surface.addEventListener('pointerdown', this.onPointerDown, { passive: false });
    window.addEventListener('pointermove', this.onPointerMove, { passive: true });
    window.addEventListener('pointerup', this.onPointerUp);
    window.addEventListener('pointercancel', this.onPointerUp);
    surface.addEventListener('contextmenu', (e) => e.preventDefault());
    if (typeof window.DeviceOrientationEvent !== 'undefined') {
      this.tiltAvailable = true;
      window.addEventListener('deviceorientation', this.onOrientation);
    }
  }

  /** iOS needs an explicit permission prompt from inside a user gesture. Call on first tap. */
  async requestTilt(): Promise<void> {
    const DOE = window.DeviceOrientationEvent as unknown as { requestPermission?: () => Promise<'granted' | 'denied'> };
    if (DOE && typeof DOE.requestPermission === 'function' && this.tiltPermission === 'unknown') {
      try {
        this.tiltPermission = await DOE.requestPermission();
      } catch {
        this.tiltPermission = 'denied';
      }
    } else if (this.tiltPermission === 'unknown') {
      this.tiltPermission = 'granted';
    }
  }

  /** Zero the tilt at the current device angle (start of every run). */
  calibrate(): void {
    this.tiltNeutral = this.tiltRaw;
    this.tiltSmoothed = 0;
  }

  /** True when real orientation data is flowing. */
  get tiltActive(): boolean {
    return this.hasTilt && this.tiltPermission !== 'denied';
  }

  get usingTilt(): boolean {
    return this.isTouch && this.tiltActive;
  }

  /** Per render frame. */
  update(dt: number): void {
    this.pollGamepad();
    // Keyboard steering ramps like an analog stick (feels smooth, still snappy).
    const left = this.keys.has('ArrowLeft') || this.keys.has('KeyA');
    const right = this.keys.has('ArrowRight') || this.keys.has('KeyD');
    const target = (right ? 1 : 0) - (left ? 1 : 0);
    const rate = target === 0 ? 10 : Math.sign(target) !== Math.sign(this.keySteer) ? 14 : 7;
    this.keySteer += (target - this.keySteer) * Math.min(1, dt * rate);

    // Tilt: 22° of lean = full lock, small deadzone, gentle curve.
    let tilt = 0;
    if (this.usingTilt) {
      const deg = this.tiltRaw - this.tiltNeutral;
      const dz = 2.2;
      const mag = Math.max(0, Math.abs(deg) - dz) / 22;
      tilt = Math.sign(deg) * Math.min(1, mag * (0.65 + 0.35 * mag));
      this.tiltSmoothed += (tilt - this.tiltSmoothed) * Math.min(1, dt * 18);
      tilt = this.tiltSmoothed;
    }

    let steer = this.keySteer;
    if (Math.abs(tilt) > Math.abs(steer)) steer = tilt;
    if (Math.abs(this.touchSteer) > Math.abs(steer)) steer = this.touchSteer;
    if (Math.abs(this.padSteer) > Math.abs(steer)) steer = this.padSteer;
    this.steer = Math.max(-1, Math.min(1, steer));

    const pointerHold = this.pointers.size > 0;
    let keyHold = false;
    for (const k of HOLD_KEYS) if (this.keys.has(k)) keyHold = true;
    this.hold = this.enabled && (keyHold || pointerHold || this.padHold);
  }

  /** Steering quantised exactly as the sim/replay expects. */
  get steerQ(): number {
    return Math.round(this.steer * TUNING.steerSteps);
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    if (e.repeat) {
      if (e.code === 'Space' || e.code.startsWith('Arrow')) e.preventDefault();
      return;
    }
    this.keys.add(e.code);
    if (e.code === 'Space' || e.code === 'Enter') {
      e.preventDefault();
      this.onAction?.();
    }
    if (e.code === 'Escape' || e.code === 'KeyP') this.onPause?.();
    if (e.code.startsWith('Arrow')) e.preventDefault();
    if (e.code === 'ArrowLeft' || e.code === 'KeyA') this.onNav?.(-1);
    if (e.code === 'ArrowRight' || e.code === 'KeyD') this.onNav?.(1);
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    this.keys.delete(e.code);
  };

  private clear = (): void => {
    this.keys.clear();
    this.pointers.clear();
    this.touchSteer = 0;
  };

  private onPointerDown = (e: PointerEvent): void => {
    if (e.pointerType === 'touch') this.isTouch = true;
    e.preventDefault();
    this.pointers.set(e.pointerId, { x: e.clientX, startX: e.clientX });
    try {
      this.surface.setPointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    this.onAction?.();
  };

  private onPointerMove = (e: PointerEvent): void => {
    const p = this.pointers.get(e.pointerId);
    if (!p) return;
    p.x = e.clientX;
    // No tilt: the first finger's sideways drag (from where it landed) steers while it holds.
    if (e.pointerType === 'touch' && !this.usingTilt && this.pointers.keys().next().value === e.pointerId) {
      const span = Math.max(50, this.surface.clientWidth * 0.14);
      this.touchSteer = Math.max(-1, Math.min(1, (p.x - p.startX) / span));
    }
  };

  private onPointerUp = (e: PointerEvent): void => {
    if (this.pointers.keys().next().value === e.pointerId) this.touchSteer = 0;
    this.pointers.delete(e.pointerId);
  };

  private onOrientation = (e: DeviceOrientationEvent): void => {
    if (e.gamma === null || e.beta === null) return;
    this.hasTilt = true;
    const angle = (screen.orientation?.angle ?? (window as unknown as { orientation?: number }).orientation ?? 0) as number;
    let t: number;
    switch (((angle % 360) + 360) % 360) {
      case 90:
        t = e.beta;
        break;
      case 270:
        t = -e.beta;
        break;
      case 180:
        t = -e.gamma;
        break;
      default:
        t = e.gamma;
    }
    this.tiltRaw = t;
  };

  private pollGamepad(): void {
    const pads = typeof navigator.getGamepads === 'function' ? navigator.getGamepads() : [];
    let hold = false;
    let steer = 0;
    let start = false;
    for (const gp of pads) {
      if (!gp) continue;
      const x = gp.axes[0] ?? 0;
      if (Math.abs(x) > 0.12) steer = x;
      if (gp.buttons[14]?.pressed) steer = -1;
      if (gp.buttons[15]?.pressed) steer = 1;
      hold ||= !!(gp.buttons[0]?.pressed || gp.buttons[6]?.pressed || gp.buttons[7]?.pressed || gp.buttons[1]?.pressed);
      start ||= !!gp.buttons[9]?.pressed;
    }
    if (hold && !this.padHold) this.onAction?.();
    if (start && !this.padPrevStart) this.onPause?.();
    this.padPrevStart = start;
    this.padHold = hold;
    this.padSteer = steer;
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.clear);
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('pointercancel', this.onPointerUp);
    window.removeEventListener('deviceorientation', this.onOrientation);
  }
}
